import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';

export interface AgentNote {
  id: string;
  note_type: string;
  note: string;
  content: string;
  created_at: string;
  agent_id?: string;
  scope?: string;
  confidence?: number;
  metadata?: Record<string, unknown>;
  context_tags?: string[];
}

interface UseAgentNotesOptions {
  types?: string[];       // filter by note_type — undefined = all
  limit?: number;         // default 50
  pollInterval?: number;  // ms, default 60000, 0 = no polling
}

export function useAgentNotes(options: UseAgentNotesOptions = {}) {
  const { types, limit = 50, pollInterval = 60_000 } = options;
  const [notes, setNotes] = useState<AgentNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = async () => {
    if (!supabase) { setLoading(false); return; }
    // in() filter must come before order()/limit() to match mock chain
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let q: any = supabase.from('agent_notes').select('*');
    if (types && types.length > 0) {
      q = q.in('note_type', types);
    }
    const { data, error: err } = await q
      .order('created_at', { ascending: false })
      .limit(limit);
    if (err) { setError(err.message); return; }
    
    // Map data to ensure content fallback if note exists
    const mapped = (data ?? []).map((item: any) => ({
      ...item,
      content: item.content || item.note,
      note: item.note || item.content,
    }));
    
    setNotes(mapped);
    setLoading(false);
  };

  useEffect(() => {
    fetch();
    if (pollInterval > 0) {
      const id = setInterval(fetch, pollInterval);
      return () => clearInterval(id);
    }
  }, [types?.join(','), limit, pollInterval]);

  return { notes, loading, error, refetch: fetch };
}
